import * as React from 'react';
import { useAuth } from '@/auth/AuthProvider';

/**
 * The portal's announcements (operator-panel-v2 T2.7, migration 0240): `my_announcements()`, the notices
 * the operator published for a window that reach the caller's agencies. Re-read when the tab comes back
 * (visibility, window focus) and every `REFRESH_MS`; a dismissal is remembered per announcement id in
 * localStorage (a courtesy: cleared storage shows the notice again, and a non-dismissible one is never
 * hidden). A failed read keeps the last rows and logs a warning: the banner is never an error wall.
 */

export const REFRESH_MS = 5 * 60 * 1000;
export const DISMISSED_KEY = 'coreway.announcements.dismissed';
/** Ids remembered at most: a dismissal of a long-gone notice is not worth keeping. */
export const DISMISSED_MAX = 50;

export const ANNOUNCEMENT_KINDS = ['info', 'maintenance', 'terms', 'pricing'] as const;
export type AnnouncementKind = (typeof ANNOUNCEMENT_KINDS)[number];

export interface Announcement {
  id: string;
  kind: AnnouncementKind;
  title_en: string;
  title_es: string;
  body_en: string;
  body_es: string;
  starts_at: string;
  ends_at: string;
  dismissible: boolean;
}

/** The dismissed ids in storage; empty when storage is unavailable or holds garbage. */
export function readDismissed(storage: Pick<Storage, 'getItem'> | null = safeStorage()): string[] {
  try {
    const raw = storage?.getItem(DISMISSED_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** Remembers `ids` (the newest `DISMISSED_MAX`); a storage that refuses is ignored. */
export function writeDismissed(ids: readonly string[], storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  try {
    storage?.setItem(DISMISSED_KEY, JSON.stringify(ids.slice(-DISMISSED_MAX)));
  } catch {
    // Private mode, a full quota, a blocked origin: the dismissal lasts the session only.
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The rows to show: a dismissed id hides a dismissible notice only. */
export function visibleAnnouncements(rows: readonly Announcement[], dismissed: readonly string[]): Announcement[] {
  return rows.filter((r) => !r.dismissible || !dismissed.includes(r.id));
}

/** The title and body in the reader's language; Spanish when the locale is `es`, English otherwise. */
export function localizedText(row: Announcement, language: string | undefined): { title: string; body: string } {
  const es = (language ?? '').toLowerCase().startsWith('es');
  return es ? { title: row.title_es, body: row.body_es } : { title: row.title_en, body: row.body_en };
}

function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

export interface AnnouncementsState {
  /** The notices to show now (dismissed ones removed), in the server's order. */
  items: Announcement[];
  dismiss(id: string): void;
  reload(): void;
}

/** `enabled` false reads nothing (the layout passes it while the agency context is not ready). */
export function useAnnouncements(enabled = true): AnnouncementsState {
  const { client } = useAuth();
  const [rows, setRows] = React.useState<Announcement[]>([]);
  const [dismissed, setDismissed] = React.useState<string[]>(() => readDismissed());
  const seq = React.useRef(0);

  const read = React.useCallback(async () => {
    if (!enabled) return;
    const mine = ++seq.current;
    const { data, error } = await client.rpc('my_announcements');
    if (mine !== seq.current) return;
    if (error) {
      console.warn('useAnnouncements: my_announcements failed', error);
      return;
    }
    setRows(((data ?? []) as Announcement[]).filter((r) => r && typeof r.id === 'string'));
  }, [client, enabled]);
  const readRef = React.useRef(read);
  readRef.current = read;

  React.useEffect(() => {
    if (!enabled) {
      setRows([]);
      return;
    }
    void read();
  }, [read, enabled]);

  React.useEffect(() => {
    if (!enabled) return;
    const onVisibility = () => {
      if (isVisible()) void readRef.current();
    };
    const onFocus = () => void readRef.current();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    const id = setInterval(() => {
      if (isVisible()) void readRef.current();
    }, REFRESH_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      clearInterval(id);
    };
  }, [enabled]);

  const dismiss = React.useCallback((id: string) => {
    setDismissed((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      writeDismissed(next);
      return next;
    });
  }, []);

  const reload = React.useCallback(() => void readRef.current(), []);

  return { items: React.useMemo(() => visibleAnnouncements(rows, dismissed), [rows, dismissed]), dismiss, reload };
}
